import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Alert, Card, Container, Link, Stack, Typography } from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import {
  CampaignSummaryQuery,
  ExternalCampaignQuery,
} from 'components/views/_shared-queries/campaign.graphql';
import { CreateDncMutation } from 'components/views/campaign/dnc/create-dnc.graphql';
import { LeadsQuery } from 'components/views/conversation/conversation.graphql';
import { DNC_API_DOCS_URL } from 'components/views/dnc/dnc-constants';
import { DncSubmitDialog, DncSubmitDialogState } from 'components/views/dnc/dnc-submit-dialog';
import { formatDate } from 'components/views/reports/formatters';
import { CURRENT_AVAILABLE_GROUPS, useAuthContext, useHasAuthGroup } from 'providers/auth';
import { useSnackbar } from 'providers/snackbar';
import { FC, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { createSearchParams, useParams } from 'react-router';
import { CampaignSummary, CampaignSummaryVariables } from 'types/apollo/CampaignSummary';
import { CreateDnc, CreateDncVariables } from 'types/apollo/CreateDnc';
import { ExternalCampaign, ExternalCampaignVariables } from 'types/apollo/ExternalCampaign';
import { Leads, LeadsVariables } from 'types/apollo/Leads';
import { FormTextField } from '../../forms/form-text-field';

interface FormData {
  phone: string;
}

const phoneRegExp = /^\+\d{11}$/;

// Reports page's date-picker min is 2020-03-09 ("beginning of Aktify" per
// reports/filters.tsx). Starting one day past it keeps the deep link inside the
// picker's range; no leads exist that early anyway.
const REPORTS_START_DATE = '2020-3-10';

// Same cap as CompanyDnc: distinct campaigns for one phone stay well under it.
const LEADS_FETCH_LIMIT = 500;

export const CampaignDnc: FC = () => {
  const { campaignId: urlCampaignId, id: urlCompanyId } = useParams();
  const { isInternal } = useAuthContext();
  const canCreateDnc = useHasAuthGroup(CURRENT_AVAILABLE_GROUPS.CustomerAdmin);
  const canShowForm = isInternal || canCreateDnc;
  const { openSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogState, setDialogState] = useState<DncSubmitDialogState>('confirm');
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  // null = unknown (lookup failed): keep the conversation link, skip the warning.
  const [phoneIsLead, setPhoneIsLead] = useState<boolean | null>(null);

  const { data: internalData } = useQuery<CampaignSummary, CampaignSummaryVariables>(
    CampaignSummaryQuery,
    {
      variables: { campaignId: urlCampaignId ?? '0' },
      skip: !isInternal || !urlCampaignId,
    },
  );

  const { data: externalData } = useQuery<ExternalCampaign, ExternalCampaignVariables>(
    ExternalCampaignQuery,
    {
      variables: { campaignId: urlCampaignId ?? '' },
      skip: isInternal || !urlCampaignId,
    },
  );

  const campaignShortId = isInternal
    ? internalData?.campaign?.shortId
    : externalData?.externalCampaign?.campaignId;
  const campaignIdForMutation = campaignShortId;
  const companyName = internalData?.campaign?.brand?.company?.name;
  const campaignName = isInternal ? internalData?.campaign?.name : undefined;

  const form = useForm<FormData>({
    defaultValues: { phone: '' },
  });

  const [createDnc] = useMutation<CreateDnc, CreateDncVariables>(CreateDncMutation);
  const [fetchLeads] = useLazyQuery<Leads, LeadsVariables>(LeadsQuery);

  const onSubmit = form.handleSubmit((input) => {
    if (!campaignIdForMutation) return;
    setPendingPhone(input.phone);
    setDialogState('confirm');
    setDialogOpen(true);
  });

  const handleConfirm = async () => {
    if (!pendingPhone || !campaignIdForMutation) return;
    setDialogState('loading');
    try {
      await createDnc({
        variables: {
          input: {
            campaignId: campaignIdForMutation,
            // Internal users hit /companies/:id/... so urlCompanyId is the target
            // company. External users are on /campaigns/... (no companyId in URL);
            // the graph-service interceptor stamps companyId from x-company-id.
            ...(urlCompanyId && { companyId: urlCompanyId }),
            phone: pendingPhone,
          },
        },
      });
      setPhoneIsLead(await isLeadInThisCampaign(pendingPhone, campaignIdForMutation));
      setDialogState('success');
    } catch (err) {
      // GraphQLWrapper's global onError shows a snackbar. Close the dialog
      // so the user isn't stuck on the loading state after a rejected mutation.
      setDialogOpen(false);
      setDialogState('confirm');
      setPendingPhone(null);
    }
  };

  // The company-wide lookup filtered to this campaign: the per-campaign branch of externalLeads
  // rejects old start dates for companies with leadsAccessForbiddenAfterDays.
  const isLeadInThisCampaign = async (phone: string, shortId: string): Promise<boolean | null> => {
    try {
      const result = await fetchLeads({
        variables: {
          phone,
          ...(urlCompanyId && { companyId: urlCompanyId }),
          startDate: '2020-03-10T00:00:00Z',
          endDate: new Date().toISOString(),
          take: LEADS_FETCH_LIMIT,
        },
        fetchPolicy: 'network-only',
        // The DNC already succeeded; a failed lookup must not pop an error snackbar.
        context: { suppressGlobalErrorSnackbar: true },
      });
      if (result.error || !result.data) {
        return null;
      }
      return result.data.externalLeads.leads.some((lead) => lead.campaignShortId === shortId);
    } catch (_err) {
      return null;
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setDialogState('confirm');
    setPendingPhone(null);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setDialogState('confirm');
    setPendingPhone(null);
    setPhoneIsLead(null);
    form.reset();
    openSnackbar({ message: 'DNC phone successfully added', severity: 'success' });
  };

  // Success-state links. Both scoped to the current campaign for immediate
  // verification of the exact action taken.
  const { reportsHref, conversationLinks } = useMemo(() => {
    if (!pendingPhone || !campaignIdForMutation) {
      return { reportsHref: null, conversationLinks: [] };
    }

    // Reports link is intentionally unscoped from the current campaign. The
    // backend's dnc_updated_handler calls hard_dnc_by_phone_in_company, which
    // fans a "campaign-level" DNC out to every lead in the company. Scoping
    // this link to only the current campaign would hide that reality; showing
    // all campaigns makes the fan-out visible.
    const reportsParams = createSearchParams({
      start: REPORTS_START_DATE,
      end: formatDate(new Date()),
      phone: pendingPhone,
    }).toString();

    const reportsPath = isInternal ? `/companies/${urlCompanyId}/reports/dnc` : '/reports/dnc';

    const conversationParams = createSearchParams({ phone: pendingPhone }).toString();
    const conversationPath =
      isInternal && urlCompanyId
        ? `/companies/${urlCompanyId}/campaigns/${urlCampaignId}/conversation`
        : `/campaigns/${urlCampaignId}/conversation`;

    return {
      reportsHref: `${reportsPath}?${reportsParams}`,
      // No conversation exists for a phone that isn't a lead here.
      conversationLinks:
        phoneIsLead === false
          ? []
          : [
              {
                label: campaignName ?? 'View conversation',
                href: `${conversationPath}?${conversationParams}`,
              },
            ],
    };
  }, [
    pendingPhone,
    campaignIdForMutation,
    isInternal,
    urlCompanyId,
    urlCampaignId,
    campaignName,
    phoneIsLead,
  ]);

  return (
    <Container maxWidth="lg" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {canShowForm ? (
          <form onSubmit={onSubmit}>
            <FormProvider {...form}>
              <Card variant="outlined" sx={{ padding: '16px' }}>
                <Stack spacing={2}>
                  <Typography variant="h5">DNC Lead</Typography>
                  <FormTextField
                    form={form}
                    name="phone"
                    customValidate={phoneRegExp}
                    customValidateMessage="Must be properly formatted phone like +12345554321"
                    helperText="Format: +15554329876"
                    variant="standard"
                  />
                  <Typography variant="body2" color="text.secondary">
                    This form adds one phone number at a time. For bulk DNC, use the{' '}
                    <Link
                      href={DNC_API_DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#1976d2' }}
                    >
                      API reference
                    </Link>
                    .
                  </Typography>
                </Stack>
              </Card>
              <FormSubmitRow
                sx={{ marginTop: 2 }}
                onCancel={() => form.reset()}
                disabled={!campaignIdForMutation}
              />
            </FormProvider>
          </form>
        ) : (
          <Alert severity="info">
            You do not have permission to add a DNC. Contact your organization administrator.
          </Alert>
        )}
      </Stack>

      <DncSubmitDialog
        open={dialogOpen}
        state={dialogState}
        phone={pendingPhone}
        scope="campaign"
        companyName={companyName}
        reportsHref={reportsHref}
        conversationLinks={conversationLinks}
        phoneIsLead={phoneIsLead}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onClose={handleClose}
      />
    </Container>
  );
};
